import { CalendarClock, DoorOpen, History, LogIn, LogOut, Settings, Video } from "lucide-react";
import type { PropsWithChildren } from "react";
import { Link, NavLink } from "react-router";
import { useAuth } from "../features/auth/AuthContext";
import { ui } from "../lib/ui";
import { Brand } from "./Brand";

type AppShellProps = PropsWithChildren<{ landing?: boolean }>;

export function AppShell({ children, landing = false }: AppShellProps) {
  const auth = useAuth();
  const routes = [
    { to: "/", label: ui("Start", "Старт"), icon: Video, end: true },
    { to: "/join", label: ui("Join", "Войти"), icon: DoorOpen, end: false },
    ...(auth.snapshot ? [
      { to: "/schedule", label: ui("Schedule", "Расписание"), icon: CalendarClock, end: false },
      { to: "/history", label: ui("History", "История"), icon: History, end: false },
      { to: "/settings", label: ui("Settings", "Настройки"), icon: Settings, end: false },
    ] : []),
  ];

  const navigation = routes.map(({ to, label, icon: Icon, end }) => (
    <NavLink key={to} to={to} end={end}>
      <Icon aria-hidden="true" size={19} />
      <span>{label}</span>
    </NavLink>
  ));

  const landingNavigation = (
    <>
      <NavLink to="/join">{ui("Join", "Войти")}</NavLink>
      {auth.snapshot ? <NavLink to="/schedule"><CalendarClock aria-hidden="true" size={16} />{ui("Schedule", "Расписание")}</NavLink> : null}
      {auth.snapshot ? <NavLink to="/history"><History aria-hidden="true" size={16} />{ui("History", "История")}</NavLink> : null}
      {auth.snapshot ? <NavLink to="/settings"><Settings aria-hidden="true" size={16} />{ui("Settings", "Настройки")}</NavLink> : null}
    </>
  );

  return (
    <div className={`app-shell${landing ? " app-shell--landing" : ""}`}>
      <a className="skip-link" href="#main-content">{ui("Skip to content", "К содержанию")}</a>
      <header className="site-header">
        <Brand />
        {landing ? <nav aria-label={ui("Primary navigation", "Основная навигация")}>{landingNavigation}</nav> : null}
        <div className="account-control">
          {auth.loading ? <span className="account-control__loading">{ui("Checking account…", "Проверяем аккаунт…")}</span> : auth.snapshot ? (
            <>
              <span className="account-chip"><span>{auth.snapshot.user.displayName || auth.snapshot.user.handle}</span><small>@{auth.snapshot.user.handle}</small></span>
              <button className="icon-text-button" type="button" onClick={() => void auth.logout()}><LogOut aria-hidden="true" size={17} />{ui("Log out", "Выйти")}</button>
            </>
          ) : <Link className="icon-text-button" to="/login"><LogIn aria-hidden="true" size={17} />{ui("Sign in", "Войти")}</Link>}
        </div>
      </header>
      {landing ? (
        <>
          {auth.unavailable ? <div className="service-strip" role="status">{ui("Account service is unavailable. You can still try to join an existing room.", "Сервис аккаунтов недоступен. В существующую комнату всё ещё можно попробовать войти.")}</div> : null}
          {children}
        </>
      ) : (
        <div className="app-frame">
          <aside className="app-rail">
            <nav aria-label={ui("Primary navigation", "Основная навигация")}>{navigation}</nav>
            <p><Video aria-hidden="true" /><strong>{ui("Browser rooms", "Комнаты в браузере")}</strong><span>{ui("No installation required", "Установка не нужна")}</span></p>
          </aside>
          <div className="app-workspace">
            {auth.unavailable ? <div className="service-strip" role="status">{ui("Account service is unavailable. You can still try to join an existing room.", "Сервис аккаунтов недоступен. В существующую комнату всё ещё можно попробовать войти.")}</div> : null}
            {children}
          </div>
        </div>
      )}
      {landing ? null : <nav className="mobile-nav" aria-label={ui("Mobile navigation", "Навигация")}>{navigation}</nav>}
    </div>
  );
}
